import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Languages } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { LogoSeal } from "./Logo";

const hashLinks = [
  { label: "Solution", hi: "समाधान", href: "#solution" },
  { label: "Industries", hi: "उद्योग", href: "#industries" },
  { label: "ROI", hi: "ROI", href: "#roi" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t, lang, toggle } = useLang();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On home use in-page anchors (lenis smooth-scrolls); elsewhere route home + hash.
  const pre = isHome ? "" : "/";

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-bone/80 backdrop-blur-xl border-b border-stone/20 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
        <a href={`${pre}#top`} data-testid="logo-link" className="flex items-center gap-2.5 group">
          <span className="shrink-0" aria-hidden>
            <LogoSeal size={38} tone="dark" />
          </span>
          <span className="font-serif italic text-2xl md:text-[1.7rem] tracking-tight text-ink leading-none">
            Fresh Earth
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {hashLinks.map((l) => (
            <a
              key={l.href}
              href={`${pre}${l.href}`}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="group relative text-[0.8rem] tracking-[0.14em] uppercase text-slate hover:text-ink transition-colors duration-300"
            >
              {t(l.label, l.hi)}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-moss transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
          <Link
            to="/case-studies"
            data-testid="nav-case-studies"
            className="group relative text-[0.8rem] tracking-[0.14em] uppercase text-slate hover:text-ink transition-colors duration-300"
          >
            {t("Case Studies", "केस स्टडी")}
            <span className="absolute -bottom-1 left-0 h-px w-0 bg-moss transition-all duration-300 group-hover:w-full" />
          </Link>
          <Link
            to="/resources"
            data-testid="nav-resources"
            className="group relative text-[0.8rem] tracking-[0.14em] uppercase text-slate hover:text-ink transition-colors duration-300"
          >
            {t("Resources", "संसाधन")}
            <span className="absolute -bottom-1 left-0 h-px w-0 bg-moss transition-all duration-300 group-hover:w-full" />
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            aria-label="Toggle language"
            className="inline-flex items-center gap-1.5 text-[0.72rem] tracking-[0.12em] uppercase text-slate hover:text-ink border border-stone/30 rounded-full px-3 py-2 transition-colors duration-300"
          >
            <Languages size={14} /> {lang === "en" ? "हिंदी" : "EN"}
          </button>
          <a
            href={`${pre}#contact`}
            data-testid="nav-cta-demo"
            className="hidden md:inline-flex items-center rounded-full bg-forest text-bone text-[0.78rem] tracking-[0.12em] uppercase px-6 py-3 hover:bg-moss transition-colors duration-300"
          >
            {t("Request a Pilot", "पायलट का अनुरोध करें")}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-ink p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-bone border-t border-stone/20 mt-3"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {hashLinks.map((l) => (
                <a
                  key={l.href}
                  href={`${pre}${l.href}`}
                  onClick={() => setOpen(false)}
                  className="text-sm tracking-[0.14em] uppercase text-slate"
                >
                  {t(l.label, l.hi)}
                </a>
              ))}
              <Link
                to="/case-studies"
                onClick={() => setOpen(false)}
                className="text-sm tracking-[0.14em] uppercase text-slate"
              >
                {t("Case Studies", "केस स्टडी")}
              </Link>
              <Link
                to="/resources"
                onClick={() => setOpen(false)}
                className="text-sm tracking-[0.14em] uppercase text-slate"
              >
                {t("Resources", "संसाधन")}
              </Link>
              <a
                href={`${pre}#contact`}
                onClick={() => setOpen(false)}
                className="inline-flex w-fit items-center rounded-full bg-forest text-bone text-xs tracking-[0.12em] uppercase px-6 py-3"
              >
                {t("Request a Pilot", "पायलट का अनुरोध करें")}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
